import { supabase } from '../lib/supabase'

/**
 * Admin Service - Backend API functions for admin dashboard
 * Handles all admin-related database operations with proper error handling
 */

/**
 * Fetch all courses with instructor information
 * @returns {Promise<Array>} List of all courses
 */
export async function fetchAllCourses() {
  try {
    const { data: courses, error } = await supabase
      .from('courses')
      .select(`
        *,
        profiles!instructor_id(id, first_name, last_name, email),
        enrollments(count)
      `)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching courses:', error)
      throw new Error(`Failed to fetch courses: ${error.message}`)
    }

    // Sort by display_order client-side (works whether or not the column exists)
    if (courses && courses.length > 0 && courses[0].display_order !== undefined) {
      courses.sort((a, b) => {
        const orderA = a.display_order ?? Number.MAX_SAFE_INTEGER
        const orderB = b.display_order ?? Number.MAX_SAFE_INTEGER
        if (orderA !== orderB) return orderA - orderB
        return new Date(b.created_at) - new Date(a.created_at)
      })
    }

    // Transform data to include instructor name and enrollment count
    const transformedCourses = (courses || []).map(course => ({
      ...course,
      instructor_name: course.profiles 
        ? `${course.profiles.first_name} ${course.profiles.last_name}`.trim()
        : 'Unknown',
      instructor_email: course.profiles?.email || '',
      enrollment_count: course.enrollments?.[0]?.count || 0
    }))

    return transformedCourses
  } catch (error) {
    console.error('Error in fetchAllCourses:', error)
    throw error
  }
}

/**
 * Fetch all instructors with statistics
 * @returns {Promise<Array>} List of all instructors
 */
export async function fetchAllInstructors() {
  try {
    // First, fetch all instructors
    const { data: instructors, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'instructor')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching instructors:', error)
      throw new Error(`Failed to fetch instructors: ${error.message}`)
    }

    if (!instructors || instructors.length === 0) {
      return []
    }

    // Fetch courses count for each instructor
    const instructorIds = instructors.map(i => i.id)
    const { data: courses, error: coursesError } = await supabase
      .from('courses')
      .select('instructor_id, id')
      .in('instructor_id', instructorIds)

    if (coursesError) {
      console.error('Error fetching courses:', coursesError)
    }

    // Fetch enrollments for all courses
    const courseIds = courses?.map(c => c.id) || []
    let enrollments = []
    if (courseIds.length > 0) {
      const { data: enrollmentsData, error: enrollmentsError } = await supabase
        .from('enrollments')
        .select('course_id, student_id')
        .in('course_id', courseIds)

      if (enrollmentsError) {
        console.error('Error fetching enrollments:', enrollmentsError)
      } else {
        enrollments = enrollmentsData || []
      }
    }

    // Calculate statistics for each instructor
    const transformedInstructors = instructors.map(instructor => {
      // Count courses for this instructor
      const instructorCourses = courses?.filter(c => c.instructor_id === instructor.id) || []
      const coursesCount = instructorCourses.length

      // Count unique students across all instructor's courses
      const instructorCourseIds = instructorCourses.map(c => c.id)
      const uniqueStudents = new Set(
        enrollments
          .filter(e => instructorCourseIds.includes(e.course_id))
          .map(e => e.student_id)
      )

      return {
        ...instructor,
        courses_count: coursesCount,
        students_taught: uniqueStudents.size
      }
    })

    return transformedInstructors
  } catch (error) {
    console.error('Error in fetchAllInstructors:', error)
    throw error
  }
}

/**
 * Fetch all students with enrollment information
 * @returns {Promise<Array>} List of all students
 */
export async function fetchAllStudents() {
  try {
    const { data: students, error } = await supabase
      .from('profiles')
      .select(`
        *,
        enrollments(count)
      `)
      .eq('role', 'student')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching students:', error)
      throw new Error(`Failed to fetch students: ${error.message}`)
    }

    // Transform data to include enrollment count
    const transformedStudents = (students || []).map(student => ({
      ...student,
      enrollments_count: student.enrollments?.[0]?.count || 0
    }))

    return transformedStudents
  } catch (error) {
    console.error('Error in fetchAllStudents:', error)
    throw error
  }
}

/**
 * Fetch all enrollments with student and course information
 * @returns {Promise<Array>} List of all enrollments
 */
export async function fetchAllEnrollments() {
  try {
    const { data: enrollments, error } = await supabase
      .from('enrollments')
      .select(`
        *,
        profiles!student_id(id, first_name, last_name, email),
        courses(id, title, instructor_id)
      `)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching enrollments:', error)
      throw new Error(`Failed to fetch enrollments: ${error.message}`)
    }

    // Transform data to include student and course names
    const transformedEnrollments = (enrollments || []).map(enrollment => ({
      ...enrollment,
      student_name: enrollment.profiles 
        ? `${enrollment.profiles.first_name} ${enrollment.profiles.last_name}`.trim()
        : 'Unknown',
      student_email: enrollment.profiles?.email || '',
      course_title: enrollment.courses?.title || 'Unknown Course'
    }))

    return transformedEnrollments
  } catch (error) {
    console.error('Error in fetchAllEnrollments:', error)
    throw error
  }
}

/**
 * Fetch all sessions with course information
 * @returns {Promise<Array>} List of all sessions
 */
export async function fetchAllSessions() {
  try {
    const { data: sessions, error } = await supabase
      .from('sessions')
      .select(`
        *,
        courses(id, title, instructor_id)
      `)
      .order('session_date', { ascending: false })

    if (error) {
      console.error('Error fetching sessions:', error)
      throw new Error(`Failed to fetch sessions: ${error.message}`)
    }

    // Transform data to include course title
    const transformedSessions = (sessions || []).map(session => ({
      ...session,
      course_title: session.courses?.title || 'Unknown Course'
    }))

    return transformedSessions
  } catch (error) {
    console.error('Error in fetchAllSessions:', error)
    throw error
  }
}

/**
 * Update a course
 * @param {string} courseId - Course ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated course
 */
export async function updateCourse(courseId, updates) {
  try {
    const { data, error } = await supabase
      .from('courses')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', courseId)
      .select()
      .single()

    if (error) {
      console.error('Error updating course:', error)
      throw new Error(`Failed to update course: ${error.message}`)
    }

    return data
  } catch (error) {
    console.error('Error in updateCourse:', error)
    throw error
  }
}

/**
 * Delete a course and its dependent records
 * @param {string} courseId - Course ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteCourse(courseId) {
  try {
    // Step 1: Delete dependent enrollments first
    const { error: enrollmentsError } = await supabase
      .from('enrollments')
      .delete()
      .eq('course_id', courseId)

    if (enrollmentsError) {
      console.error('Error deleting enrollments:', enrollmentsError)
      throw new Error(`Failed to delete course enrollments: ${enrollmentsError.message}`)
    }

    // Step 2: Delete dependent sessions
    const { error: sessionsError } = await supabase
      .from('sessions')
      .delete()
      .eq('course_id', courseId)

    if (sessionsError) {
      console.error('Error deleting sessions:', sessionsError)
      throw new Error(`Failed to delete course sessions: ${sessionsError.message}`)
    }

    // Step 3: Delete dependent homework
    const { error: homeworkError } = await supabase
      .from('homework')
      .delete()
      .eq('course_id', courseId)

    if (homeworkError) {
      console.error('Error deleting homework:', homeworkError)
      // Non-critical, continue
    }

    // Step 4: Delete dependent resources
    const { error: resourcesError } = await supabase
      .from('resources')
      .delete()
      .eq('course_id', courseId)

    if (resourcesError) {
      console.error('Error deleting resources:', resourcesError)
      // Non-critical, continue
    }

    // Step 5: Delete dependent messages
    const { error: messagesError } = await supabase
      .from('messages')
      .delete()
      .eq('course_id', courseId)

    if (messagesError) {
      console.error('Error deleting messages:', messagesError)
      // Non-critical, continue
    }

    // Step 6: Delete dependent reviews
    const { error: reviewsError } = await supabase
      .from('reviews')
      .delete()
      .eq('course_id', courseId)

    if (reviewsError) {
      console.error('Error deleting reviews:', reviewsError)
      // Non-critical, continue
    }

    // Step 7: Now delete the course itself
    // First verify the course exists and we can see it
    const { data: courseCheck } = await supabase
      .from('courses')
      .select('id')
      .eq('id', courseId)
      .single()

    if (!courseCheck) {
      throw new Error('Course not found or already deleted.')
    }

    const { error } = await supabase
      .from('courses')
      .delete()
      .eq('id', courseId)

    if (error) {
      console.error('Error deleting course:', error)
      throw new Error(`Failed to delete course: ${error.message}`)
    }

    // Verify the course was actually deleted
    const { data: verifyCheck } = await supabase
      .from('courses')
      .select('id')
      .eq('id', courseId)
      .maybeSingle()

    if (verifyCheck) {
      // Course still exists - RLS likely blocked the delete
      console.error('Course still exists after delete attempt - RLS may be blocking')
      throw new Error('Delete was blocked by database permissions. Please check that your admin role has delete access to courses.')
    }

    return true
  } catch (error) {
    console.error('Error in deleteCourse:', error)
    throw error
  }
}

/**
 * Update an instructor profile
 * @param {string} instructorId - Instructor ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated instructor
 */
export async function updateInstructor(instructorId, updates) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', instructorId)
      .select()
      .single()

    if (error) {
      console.error('Error updating instructor:', error)
      throw new Error(`Failed to update instructor: ${error.message}`)
    }

    return data
  } catch (error) {
    console.error('Error in updateInstructor:', error)
    throw error
  }
}

/**
 * Suspend an instructor (unpublish all courses and disable account)
 * @param {string} instructorId - Instructor ID
 * @returns {Promise<boolean>} Success status
 */
export async function suspendInstructor(instructorId) {
  try {
    // Unpublish all instructor's courses
    const { error: coursesError } = await supabase
      .from('courses')
      .update({ 
        is_published: false,
        status: 'suspended'
      })
      .eq('instructor_id', instructorId)

    if (coursesError) {
      console.error('Error unpublishing courses:', coursesError)
      throw new Error(`Failed to unpublish courses: ${coursesError.message}`)
    }

    // Update instructor status
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ 
        is_suspended: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', instructorId)

    if (profileError) {
      console.error('Error suspending instructor:', profileError)
      throw new Error(`Failed to suspend instructor: ${profileError.message}`)
    }

    return true
  } catch (error) {
    console.error('Error in suspendInstructor:', error)
    throw error
  }
}

/**
 * Update a student profile
 * @param {string} studentId - Student ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated student
 */
export async function updateStudent(studentId, updates) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', studentId)
      .select()
      .single()

    if (error) {
      console.error('Error updating student:', error)
      throw new Error(`Failed to update student: ${error.message}`)
    }

    return data
  } catch (error) {
    console.error('Error in updateStudent:', error)
    throw error
  }
}

/**
 * Update enrollment status
 * @param {string} enrollmentId - Enrollment ID
 * @param {string} status - New status (active, completed, dropped, pending)
 * @returns {Promise<Object>} Updated enrollment
 */
export async function updateEnrollmentStatus(enrollmentId, status) {
  try {
    const { data, error } = await supabase
      .from('enrollments')
      .update({
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', enrollmentId)
      .select()
      .single()

    if (error) {
      console.error('Error updating enrollment:', error)
      throw new Error(`Failed to update enrollment: ${error.message}`)
    }

    return data
  } catch (error) {
    console.error('Error in updateEnrollmentStatus:', error)
    throw error
  }
}

/**
 * Update a session
 * @param {string} sessionId - Session ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated session
 */
export async function updateSession(sessionId, updates) {
  try {
    const { data, error } = await supabase
      .from('sessions')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', sessionId)
      .select()
      .single()

    if (error) {
      console.error('Error updating session:', error)
      throw new Error(`Failed to update session: ${error.message}`)
    }

    return data
  } catch (error) {
    console.error('Error in updateSession:', error)
    throw error
  }
}

/**
 * Delete a session
 * @param {string} sessionId - Session ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteSession(sessionId) {
  try {
    const { error } = await supabase
      .from('sessions')
      .delete()
      .eq('id', sessionId)

    if (error) {
      console.error('Error deleting session:', error)
      throw new Error(`Failed to delete session: ${error.message}`)
    }

    return true
  } catch (error) {
    console.error('Error in deleteSession:', error)
    throw error
  }
}

/**
 * Fetch course enrollments with student neurodiversity information
 * @returns {Promise<Array>} List of enrollments with student details
 */
export async function fetchCourseEnrollments() {
  try {
    const { data: enrollments, error } = await supabase
      .from('enrollments')
      .select(`
        *,
        profiles!student_id(
          id,
          first_name,
          last_name,
          email,
          age,
          grade_level,
          neurodiversity_profile,
          other_needs,
          interests
        ),
        courses(
          id,
          title,
          profiles!instructor_id(first_name, last_name)
        )
      `)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching course enrollments:', error)
      throw new Error(`Failed to fetch enrollments: ${error.message}`)
    }

    // Transform data to flatten structure
    const transformedEnrollments = (enrollments || []).map(enrollment => ({
      id: enrollment.id,
      course_id: enrollment.course_id,
      student_id: enrollment.student_id,
      status: enrollment.status,
      created_at: enrollment.created_at,
      course_title: enrollment.courses?.title || 'Unknown Course',
      instructor_name: enrollment.courses?.profiles 
        ? `${enrollment.courses.profiles.first_name} ${enrollment.courses.profiles.last_name}`.trim()
        : 'Unknown',
      student_name: enrollment.profiles 
        ? `${enrollment.profiles.first_name} ${enrollment.profiles.last_name}`.trim()
        : 'Unknown',
      student_email: enrollment.profiles?.email || '',
      age: enrollment.profiles?.age,
      grade_level: enrollment.profiles?.grade_level,
      neurodiversity_profile: enrollment.profiles?.neurodiversity_profile || [],
      other_needs: enrollment.profiles?.other_needs,
      interests: enrollment.profiles?.interests || []
    }))

    return transformedEnrollments
  } catch (error) {
    console.error('Error in fetchCourseEnrollments:', error)
    throw error
  }
}

/**
 * Send bulk email to students in a course
 * @param {Object} emailData - Email data
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.message - Email message body
 * @param {string} emailData.courseTitle - Course title
 * @param {string} emailData.courseId - Course ID
 * @param {string[]} emailData.recipientEmails - Array of recipient email addresses
 * @param {string} emailData.senderName - Name of the sender (admin)
 * @returns {Promise<Object>} Result with emailsSent count
 */
export async function sendBulkEmailToStudents(emailData) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-bulk-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(emailData)
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(`Email function error: ${JSON.stringify(error)}`)
    }

    const result = await response.json()
    return result
  } catch (error) {
    console.error('Error sending bulk email:', error)
    throw error
  }
}

/**
 * Update the display order of courses (for drag-and-drop reordering)
 * @param {Array<{id: string, display_order: number}>} courseOrders - Array of course IDs with their new display_order
 * @returns {Promise<boolean>} Success status
 */
export async function updateCourseOrder(courseOrders) {
  try {
    // Update each course's display_order
    const updatePromises = courseOrders.map(({ id, display_order }) =>
      supabase
        .from('courses')
        .update({ display_order, updated_at: new Date().toISOString() })
        .eq('id', id)
    )

    const results = await Promise.all(updatePromises)
    const errors = results.filter(r => r.error)

    if (errors.length > 0) {
      console.error('Some course order updates failed:', errors.map(e => e.error))
      throw new Error(`Failed to update ${errors.length} course(s) order`)
    }

    return true
  } catch (error) {
    console.error('Error in updateCourseOrder:', error)
    throw error
  }
}

/**
 * Fetch dashboard statistics
 * @returns {Promise<Object>} Dashboard statistics
 */
export async function fetchDashboardStats() {
  try {
    // Fetch counts for all entities
    const [coursesResult, instructorsResult, studentsResult, enrollmentsResult, topicsResult, careResourcesResult, questionsResult, reviewsResult] = await Promise.all([
      supabase.from('courses').select('id', { count: 'exact', head: true }),
      supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'instructor'),
      supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'student'),
      supabase.from('enrollments').select('id', { count: 'exact', head: true }),
      supabase.from('topics').select('id', { count: 'exact', head: true }),
      supabase.from('care_resources').select('id', { count: 'exact', head: true }),
      supabase.from('questions').select('id', { count: 'exact', head: true }),
      supabase.from('care_reviews').select('id', { count: 'exact', head: true })
    ])

    return {
      total_courses: coursesResult.count || 0,
      total_instructors: instructorsResult.count || 0,
      total_students: studentsResult.count || 0,
      total_enrollments: enrollmentsResult.count || 0,
      total_topics: topicsResult.count || 0,
      total_care_resources: careResourcesResult.count || 0,
      total_questions: questionsResult.count || 0,
      total_reviews: reviewsResult.count || 0
    }
  } catch (error) {
    console.error('Error fetching dashboard stats:', error)
    throw error
  }
}

/**
 * Fetch recent platform activities
 * Aggregates recent events from multiple tables to create an activity feed
 * @returns {Promise<Array>} List of 20 most recent activities
 */
export async function fetchRecentActivities() {
  try {
    // Fetch recent courses
    const { data: recentCourses, error: coursesError } = await supabase
      .from('courses')
      .select('id, title, created_at, profiles!instructor_id(first_name, last_name)')
      .order('created_at', { ascending: false })
      .limit(10)

    if (coursesError) {
      console.error('Error fetching recent courses:', coursesError)
    }

    // Fetch recent enrollments
    const { data: recentEnrollments, error: enrollmentsError } = await supabase
      .from('enrollments')
      .select('id, created_at, profiles!student_id(first_name, last_name), courses(title)')
      .order('created_at', { ascending: false })
      .limit(10)

    if (enrollmentsError) {
      console.error('Error fetching recent enrollments:', enrollmentsError)
    }

    // Fetch recent sessions
    const { data: recentSessions, error: sessionsError } = await supabase
      .from('sessions')
      .select('id, title, created_at, courses(title, profiles!instructor_id(first_name, last_name))')
      .order('created_at', { ascending: false })
      .limit(10)

    if (sessionsError) {
      console.error('Error fetching recent sessions:', sessionsError)
    }

    // Fetch recent user registrations
    const { data: recentUsers, error: usersError } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, role, created_at')
      .order('created_at', { ascending: false })
      .limit(10)

    if (usersError) {
      console.error('Error fetching recent users:', usersError)
    }

    // Fetch recent community questions
    const { data: recentQuestions, error: questionsError } = await supabase
      .from('questions')
      .select('id, title, created_at, is_anonymous, profiles!author_id(first_name, last_name), topics(name)')
      .order('created_at', { ascending: false })
      .limit(10)

    if (questionsError) {
      console.error('Error fetching recent questions:', questionsError)
    }

    // Fetch recent community answers
    const { data: recentAnswers, error: answersError } = await supabase
      .from('answers')
      .select('id, created_at, is_anonymous, profiles!author_id(first_name, last_name), questions(title)')
      .order('created_at', { ascending: false })
      .limit(10)

    if (answersError) {
      console.error('Error fetching recent answers:', answersError)
    }

    // Fetch recent care reviews
    const { data: recentReviews, error: reviewsError } = await supabase
      .from('care_reviews')
      .select('id, reviewer_name, rating, created_at, care_resources(name)')
      .order('created_at', { ascending: false })
      .limit(10)

    if (reviewsError) {
      console.error('Error fetching recent reviews:', reviewsError)
    }

    // Transform and combine all activities
    const activities = []

    // Add course creation activities
    if (recentCourses) {
      recentCourses.forEach(course => {
        const instructorName = course.profiles 
          ? `${course.profiles.first_name} ${course.profiles.last_name}`.trim()
          : 'Unknown'
        
        activities.push({
          id: `course-${course.id}`,
          type: 'course_created',
          description: `New course created: "${course.title}"`,
          user_name: instructorName,
          timestamp: course.created_at
        })
      })
    }

    // Add enrollment activities
    if (recentEnrollments) {
      recentEnrollments.forEach(enrollment => {
        const studentName = enrollment.profiles 
          ? `${enrollment.profiles.first_name} ${enrollment.profiles.last_name}`.trim()
          : 'Unknown'
        const courseTitle = enrollment.courses?.title || 'Unknown Course'
        
        activities.push({
          id: `enrollment-${enrollment.id}`,
          type: 'enrollment',
          description: `Student enrolled in "${courseTitle}"`,
          user_name: studentName,
          timestamp: enrollment.created_at
        })
      })
    }

    // Add session scheduling activities
    if (recentSessions) {
      recentSessions.forEach(session => {
        const instructorName = session.courses?.profiles 
          ? `${session.courses.profiles.first_name} ${session.courses.profiles.last_name}`.trim()
          : 'Unknown'
        const courseTitle = session.courses?.title || 'Unknown Course'
        
        activities.push({
          id: `session-${session.id}`,
          type: 'session_scheduled',
          description: `Session scheduled: "${session.title}" for ${courseTitle}`,
          user_name: instructorName,
          timestamp: session.created_at
        })
      })
    }

    // Add user registration activities
    if (recentUsers) {
      recentUsers.forEach(user => {
        const userName = `${user.first_name} ${user.last_name}`.trim()
        const roleLabel = user.role === 'instructor' ? 'Instructor' : user.role === 'student' ? 'Student' : 'User'
        
        activities.push({
          id: `user-${user.id}`,
          type: 'user_registered',
          description: `${roleLabel} registered`,
          user_name: userName,
          timestamp: user.created_at
        })
      })
    }

    // Add community question activities
    if (recentQuestions) {
      recentQuestions.forEach(question => {
        const authorName = question.is_anonymous 
          ? 'Anonymous'
          : question.profiles 
            ? `${question.profiles.first_name} ${question.profiles.last_name}`.trim()
            : 'Unknown'
        const topicName = question.topics?.name || 'Unknown Topic'
        
        activities.push({
          id: `question-${question.id}`,
          type: 'question_posted',
          description: `New question in ${topicName}: "${question.title.substring(0, 50)}${question.title.length > 50 ? '...' : ''}"`,
          user_name: authorName,
          timestamp: question.created_at
        })
      })
    }

    // Add community answer activities
    if (recentAnswers) {
      recentAnswers.forEach(answer => {
        const authorName = answer.is_anonymous 
          ? 'Anonymous'
          : answer.profiles 
            ? `${answer.profiles.first_name} ${answer.profiles.last_name}`.trim()
            : 'Unknown'
        const questionTitle = answer.questions?.title || 'Unknown Question'
        
        activities.push({
          id: `answer-${answer.id}`,
          type: 'answer_posted',
          description: `Answered question: "${questionTitle.substring(0, 40)}${questionTitle.length > 40 ? '...' : ''}"`,
          user_name: authorName,
          timestamp: answer.created_at
        })
      })
    }

    // Add care review activities
    if (recentReviews) {
      recentReviews.forEach(review => {
        const reviewerName = review.reviewer_name || 'Anonymous'
        const resourceName = review.care_resources?.name || 'Unknown Resource'
        const stars = '⭐'.repeat(review.rating)
        
        activities.push({
          id: `review-${review.id}`,
          type: 'review_submitted',
          description: `Reviewed "${resourceName}" ${stars}`,
          user_name: reviewerName,
          timestamp: review.created_at
        })
      })
    }

    // Sort all activities by timestamp (most recent first) and take top 20
    activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    
    return activities.slice(0, 20)
  } catch (error) {
    console.error('Error in fetchRecentActivities:', error)
    throw error
  }
}
